import { useState, useEffect, useRef } from 'react';
import { api } from '../../api';
import type { BusinessContext, TargetKeywordsResponse } from '../../types';

interface Props {
  domainId: string;
  domain: string;
}

function confidenceLabel(score: number): { label: string; colour: string } {
  if (score >= 80) return { label: 'High', colour: 'text-brand-green' };
  if (score >= 60) return { label: 'Medium', colour: 'text-amber-500' };
  return { label: 'Low', colour: 'text-red-400' };
}

const BUSINESS_MODEL_OPTIONS = [
  { value: '', label: '— not set —' },
  { value: 'saas',         label: 'SaaS' },
  { value: 'service',      label: 'Service / agency' },
  { value: 'ecommerce',    label: 'E-commerce' },
  { value: 'publication',  label: 'Publication / media' },
  { value: 'local_only',   label: 'Local business' },
  { value: 'mixed',        label: 'Mixed' },
] as const;

const AUDIENCE_OPTIONS = [
  { value: 'b2b',  label: 'B2B' },
  { value: 'b2c',  label: 'B2C' },
  { value: 'both', label: 'Both' },
] as const;

export function SettingsTab({ domainId, domain }: Props) {
  const [contributes, setContributes] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [bc, setBc] = useState<BusinessContext | null | undefined>(undefined);

  // Target keywords state
  const [kw, setKw] = useState<TargetKeywordsResponse | null | undefined>(undefined);
  const [kwInput, setKwInput] = useState('');
  const [kwSaving, setKwSaving] = useState(false);
  const [kwError, setKwError] = useState('');
  const [kwSaved, setKwSaved] = useState(false);
  const kwInputRef = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState(false);
  const [bcSaving, setBcSaving] = useState(false);
  const [bcError, setBcError] = useState('');
  const [bcSaved, setBcSaved] = useState(false);
  const [form, setForm] = useState({
    coreOffering: '',
    coreOfferingCategory: '',
    geographyCity: '',
    geographyCountry: '',
    businessModel: '' as string,
    targetAudience: 'b2b' as 'b2b' | 'b2c' | 'both',
  });

  useEffect(() => {
    api.domains.getBusinessContext(domainId)
      .then(setBc)
      .catch(() => setBc(null));
    api.domains.getTargetKeywords(domainId)
      .then(setKw)
      .catch(() => setKw(null));
  }, [domainId]);

  async function handleAddKeyword() {
    const keyword = kwInput.trim();
    if (!keyword || !kw) return;
    if (kw.keywords.length >= kw.maxKeywords) {
      setKwError(`Your plan allows up to ${kw.maxKeywords} keywords.`);
      return;
    }
    if (kw.keywords.includes(keyword)) {
      setKwInput('');
      kwInputRef.current?.focus();
      return;
    }
    const next = [...kw.keywords, keyword];
    await saveKeywords(next);
    setKwInput('');
    kwInputRef.current?.focus();
  }

  async function handleRemoveKeyword(keyword: string) {
    if (!kw) return;
    await saveKeywords(kw.keywords.filter((k) => k !== keyword));
  }

  async function saveKeywords(keywords: string[]) {
    if (!kw) return;
    setKwSaving(true);
    setKwError('');
    setKwSaved(false);
    try {
      const updated = await api.domains.putTargetKeywords(domainId, keywords);
      setKw(updated);
      setKwSaved(true);
      setTimeout(() => setKwSaved(false), 3000);
    } catch (err) {
      setKwError(err instanceof Error ? err.message : 'Failed to save keywords');
    } finally {
      setKwSaving(false);
    }
  }

  function startEdit() {
    if (!bc) return;
    setForm({
      coreOffering:         bc.coreOffering,
      coreOfferingCategory: bc.coreOfferingCategory,
      geographyCity:        bc.geographyCity,
      geographyCountry:     bc.geographyCountry,
      businessModel:        bc.businessModel ?? '',
      targetAudience:       bc.targetAudience,
    });
    setBcError('');
    setBcSaved(false);
    setEditing(true);
  }

  async function handleBcSave() {
    setBcSaving(true);
    setBcError('');
    setBcSaved(false);
    try {
      const updated = await api.domains.patchBusinessContext(domainId, {
        coreOffering:         form.coreOffering.trim(),
        coreOfferingCategory: form.coreOfferingCategory.trim(),
        geographyCity:        form.geographyCity.trim(),
        geographyCountry:     form.geographyCountry.trim().toUpperCase().slice(0, 2),
        businessModel:        form.businessModel || null,
      });
      if (form.targetAudience !== bc?.targetAudience) {
        const withAudience = await api.domains.patchAudienceContext(domainId, form.targetAudience);
        setBc({ ...withAudience });
      } else {
        setBc(updated);
      }
      setBcSaved(true);
      setEditing(false);
    } catch (err) {
      setBcError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setBcSaving(false);
    }
  }

  async function handleToggle(next: boolean) {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await api.domains.setAggregateConsent(domainId, next);
      setContributes(next);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save setting');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6 py-2">

      {/* Business Context */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <h3 className="font-medium text-brand-ink text-sm mb-3">Business context</h3>
        {bc === undefined && (
          <div className="h-4 w-32 bg-gray-100 rounded animate-pulse" />
        )}
        {bc === null && (
          <p className="text-xs text-gray-400">
            No business context extracted yet. Run an audit to populate this automatically.
          </p>
        )}
        {bc && !editing && (() => {
          const { label, colour } = confidenceLabel(bc.confidenceScore);
          return (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
                <div>
                  <span className="text-gray-400 block">Core offering</span>
                  <span className="text-brand-ink font-medium">{bc.coreOffering}</span>
                </div>
                <div>
                  <span className="text-gray-400 block">Category</span>
                  <span className="text-brand-ink font-medium">{bc.coreOfferingCategory.replace(/_/g, ' ')}</span>
                </div>
                {bc.geographyCity && (
                  <div>
                    <span className="text-gray-400 block">City</span>
                    <span className="text-brand-ink font-medium">{bc.geographyCity}</span>
                  </div>
                )}
                {bc.geographyCountry && (
                  <div>
                    <span className="text-gray-400 block">Country</span>
                    <span className="text-brand-ink font-medium">{bc.geographyCountry}</span>
                  </div>
                )}
                {bc.businessModel && (
                  <div>
                    <span className="text-gray-400 block">Business model</span>
                    <span className="text-brand-ink font-medium">{bc.businessModel.replace(/_/g, ' ')}</span>
                  </div>
                )}
                <div>
                  <span className="text-gray-400 block">Audience</span>
                  <span className="text-brand-ink font-medium">{bc.targetAudience.toUpperCase()}</span>
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                <span className="text-xs text-gray-400">
                  Extraction confidence —{' '}
                  <span className={`font-medium ${colour}`}>{label} ({bc.confidenceScore}/100)</span>
                  {bc.customerConfirmedAt && <span className="ml-1 text-green-600"> · manually confirmed</span>}
                </span>
                <button
                  type="button"
                  onClick={startEdit}
                  className="text-xs text-brand-green hover:underline"
                >
                  Edit
                </button>
              </div>
              {bcSaved && (
                <p className="text-xs text-brand-green">Profile saved. Schema updates on next audit.</p>
              )}
            </div>
          );
        })()}
        {bc && editing && (
          <div className="space-y-3">
            <div className="space-y-2">
              {[
                { key: 'coreOffering',         label: 'Core offering',   hint: 'What you do — human-readable, used in schema.org Service.name' },
                { key: 'coreOfferingCategory', label: 'Category',        hint: 'Short descriptor, e.g. "Software Development" — avoid internal enum slugs' },
                { key: 'geographyCity',        label: 'City',            hint: '' },
                { key: 'geographyCountry',     label: 'Country (ISO 2)', hint: 'e.g. AU, GB, US, IE' },
              ].map(({ key, label, hint }) => (
                <div key={key}>
                  <label className="text-[11px] text-gray-400 block mb-0.5">{label}</label>
                  <input
                    type="text"
                    value={form[key as keyof typeof form] as string}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-green"
                  />
                  {hint && <p className="text-[10px] text-gray-300 mt-0.5">{hint}</p>}
                </div>
              ))}
              <div>
                <label className="text-[11px] text-gray-400 block mb-0.5">Business model</label>
                <select
                  value={form.businessModel}
                  onChange={(e) => setForm((f) => ({ ...f, businessModel: e.target.value }))}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-green bg-white"
                >
                  {BUSINESS_MODEL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] text-gray-400 block mb-0.5">Audience</label>
                <select
                  value={form.targetAudience}
                  onChange={(e) => setForm((f) => ({ ...f, targetAudience: e.target.value as 'b2b' | 'b2c' | 'both' }))}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-green bg-white"
                >
                  {AUDIENCE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
            {bcError && <p className="text-xs text-red-500">{bcError}</p>}
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => void handleBcSave()}
                disabled={bcSaving}
                className="text-xs bg-brand-green text-white px-3 py-1.5 rounded-lg hover:bg-brand-green-dark disabled:opacity-50"
              >
                {bcSaving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                disabled={bcSaving}
                className="text-xs text-gray-400 hover:text-gray-600 px-3 py-1.5"
              >
                Cancel
              </button>
            </div>
            <p className="text-[10px] text-gray-300 leading-relaxed">
              Saving locks this profile against auto-extraction. Schema updates on the next audit cache miss — no redeploy needed.
            </p>
          </div>
        )}
      </div>

      {/* Aggregate benchmarks */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="font-medium text-brand-ink text-sm">Contribute to benchmarks</h3>
            <p className="text-xs text-gray-400 mt-1 leading-relaxed">
              When enabled, your domain's audit scores are included anonymously in FixMySEO's
              industry benchmarks. These benchmarks let you see how your site compares to others in
              the same vertical and country. No personal data, domain name, or individual audit
              results are ever visible to other users — only aggregated percentile distributions.
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Only scores and methodology version are used. IP address and raw audit data are
              excluded from aggregates.
            </p>
          </div>

          {/* Toggle */}
          <button
            type="button"
            role="switch"
            aria-checked={contributes}
            aria-label="Contribute to benchmarks"
            disabled={saving}
            onClick={() => void handleToggle(!contributes)}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-green focus:ring-offset-2 disabled:opacity-50 ${
              contributes ? 'bg-brand-green' : 'bg-gray-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                contributes ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Status feedback */}
        {saved && (
          <p className="text-xs text-brand-green mt-3">
            Preference saved. All existing audits for <strong>{domain}</strong> have been updated.
          </p>
        )}
        {error && (
          <p className="text-xs text-red-500 mt-3">{error}</p>
        )}
      </div>

      {/* Target AI keywords */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-medium text-brand-ink text-sm">Target AI keywords</h3>
          {kw && (
            <span className="text-xs text-gray-400">
              {kw.keywords.length} / {kw.maxKeywords}
            </span>
          )}
        </div>
        <p className="text-xs text-gray-400 mb-4">
          The topics you want AI platforms to find you for. These replace the auto-generated
          keyword list — each keyword produces 2 AI search queries per sweep.
        </p>

        {kw === undefined && (
          <div className="h-4 w-48 bg-gray-100 rounded animate-pulse" />
        )}

        {kw !== null && kw !== undefined && (
          <>
            {/* Tag list */}
            {kw.keywords.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {kw.keywords.map((keyword) => (
                  <span
                    key={keyword}
                    className="inline-flex items-center gap-1 bg-brand-green/10 text-brand-green text-xs font-medium px-2.5 py-1 rounded-full"
                  >
                    {keyword}
                    <button
                      type="button"
                      onClick={() => void handleRemoveKeyword(keyword)}
                      disabled={kwSaving}
                      className="ml-0.5 text-brand-green/60 hover:text-brand-green disabled:opacity-40"
                      aria-label={`Remove ${keyword}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Add input */}
            {kw.keywords.length < kw.maxKeywords && (
              <div className="flex gap-2">
                <input
                  ref={kwInputRef}
                  type="text"
                  value={kwInput}
                  onChange={(e) => setKwInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleAddKeyword(); } }}
                  placeholder="e.g. technical co-founder equity"
                  disabled={kwSaving}
                  className="flex-1 text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-green/40 disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={() => void handleAddKeyword()}
                  disabled={kwSaving || !kwInput.trim()}
                  className="text-xs bg-brand-green text-white px-3 py-2 rounded-lg hover:bg-brand-green/90 disabled:opacity-40 transition-colors"
                >
                  Add
                </button>
              </div>
            )}

            {kwError && <p className="text-xs text-red-500 mt-2">{kwError}</p>}
            {kwSaved && <p className="text-xs text-brand-green mt-2">Keywords saved.</p>}
          </>
        )}
      </div>

      {/* Policy note */}
      <div className="text-xs text-gray-400 leading-relaxed px-1">
        <p>
          You can change this setting at any time. Disabling it removes this domain from future
          nightly rollups and marks all existing audits as opted-out. Re-enabling re-includes them.
        </p>
        <p className="mt-1">
          See the{' '}
          <a
            href="https://fixmyseo.io/methodology"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-green hover:underline"
          >
            methodology page
          </a>{' '}
          for full details on what is and isn't included in benchmarks.
        </p>
      </div>
    </div>
  );
}
